import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * AUX Retail product page.
 * Size selection, add to bag (Horizon cart drawer compatible), subscription,
 * gallery carousel on mobile, docked add to bag, compact panel, drawers, show more.
 */
class AuxProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    this.data = json ? JSON.parse(json.textContent) : { variants: [], options: [], strings: {} };
    this.form = this.querySelector('[data-product-form]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.atc = this.querySelector('[data-atc]');
    this.panel = this.querySelector('[data-panel]');

    this.#initOptions();
    this.#initForm();
    this.#initSubscription();
    this.#initSwatches();
    this.#initDescription();
    this.#initDrawers();
    this.#initGallery();
    this.#initDocked();
    this.#initCompactPanel();
    this.#initNotify();
  }

  /* ---------- Options and variants ---------- */

  #initOptions() {
    this.optionSets = [...this.querySelectorAll('[data-option-index]')];
    this.optionSets.forEach((set) => {
      set.addEventListener('change', () => {
        set.classList.remove('has-error');
        set.querySelector('[data-size-error]')?.setAttribute('hidden', '');
        this.#updateVariant();
      });
    });
    this.sizeSelected = this.dataset.sizePreselected === 'true';
  }

  /** Current selection per option; single-value options use the only value. */
  #selectedOptions() {
    return this.data.options.map((_, index) => {
      const set = this.optionSets.find((s) => Number(s.dataset.optionIndex) === index);
      if (!set) return null; // fixed option
      const checked = set.querySelector('input:checked');
      return checked ? checked.value : undefined;
    });
  }

  #findVariant() {
    const selected = this.#selectedOptions();
    if (selected.some((v) => v === undefined)) return null;
    return (
      this.data.variants.find((variant) =>
        variant.options.every((value, i) => selected[i] === null || selected[i] === value)
      ) || null
    );
  }

  #updateVariant() {
    const variant = this.#findVariant();
    this.sizeSelected = Boolean(variant);
    if (!variant) return;

    this.variantInput.value = variant.id;
    this.variantInput.removeAttribute('data-unselected');
    this.#setPrice(variant);

    const label = this.atc.querySelector('[data-atc-label]');
    this.atc.disabled = !variant.available;
    if (label) label.textContent = variant.available ? this.data.strings.addToBag : this.data.strings.soldOut;
    this.#toggleNotify(variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  #currentVariant() {
    const id = Number(this.variantInput?.value);
    return this.data.variants.find((v) => v.id === id) || this.data.variants[0];
  }

  #setPrice(variant = this.#currentVariant()) {
    if (!variant) return;
    const plan = this.#activePlanId();
    const price = plan && variant.plans[plan] ? variant.plans[plan] : variant.price;
    this.querySelectorAll('[data-price], [data-atc-price], [data-docked-price]').forEach((el) => (el.textContent = price));
    const compare = this.querySelector('[data-subscribe-compare]');
    const subPrice = this.querySelector('[data-subscribe-price]');
    const firstPlan = Object.values(variant.plans)[0];
    if (compare) compare.textContent = variant.price;
    if (subPrice && firstPlan) subPrice.textContent = variant.plans[this.#selectedPlanId()] || firstPlan;
  }

  /* ---------- Back in stock (Klaviyo) ---------- */

  #initNotify() {
    this.notify = this.querySelector('[data-notify]');
    if (!this.notify) return;
    const toggle = this.notify.querySelector('[data-notify-toggle]');
    const form = this.notify.querySelector('[data-notify-form]');
    toggle.addEventListener('click', () => {
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'true');
      form.hidden = false;
      form.querySelector('[data-notify-email]').focus();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#subscribeNotify(form);
    });
    // Initial state: a sold-out size is already selected, or nothing in the product is available.
    const preselected = this.sizeSelected ? this.#currentVariant() : null;
    if (preselected) this.#toggleNotify(preselected);
    else if (this.data.variants.length && !this.data.variants.some((v) => v.available)) this.#toggleNotify(this.data.variants[0]);
  }

  #toggleNotify(variant) {
    if (!this.notify) return;
    const soldOut = Boolean(variant) && !variant.available;
    this.notify.hidden = !soldOut;
    this.atc.hidden = soldOut;
    this.notifyVariant = soldOut ? variant : null;
    if (!soldOut) return;
    // Reset to the button for each newly selected sold-out size.
    this.notify.querySelector('[data-notify-toggle]').hidden = false;
    this.notify.querySelector('[data-notify-form]').hidden = true;
    this.notify.querySelector('[data-notify-done]').hidden = true;
    this.notify.querySelector('[data-notify-error]').hidden = true;
  }

  async #subscribeNotify(form) {
    const input = form.querySelector('[data-notify-email]');
    const error = form.querySelector('[data-notify-error]');
    const email = input.value.trim();
    error.hidden = true;
    if (!input.checkValidity() || !email || !this.notifyVariant) {
      error.hidden = false;
      return;
    }
    const key = this.notify.dataset.klaviyo;
    const lang = (this.notify.dataset.language || 'sv').slice(0, 2);
    const headers = { 'Content-Type': 'application/vnd.api+json', revision: '2025-01-15' };
    const base = 'https://a.klaviyo.com/client';
    const button = form.querySelector('button[type="submit"]');
    button.disabled = true;
    try {
      // Store the shopper's language so the Klaviyo flow can send the email in the right language.
      await fetch(`${base}/profiles/?company_id=${key}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email,
              // Klaviyo's flow splits on Locale (starts with sv / de, everyone else gets English).
              locale: { sv: 'sv-SE', de: 'de-DE' }[lang] || 'en-GB',
              properties: { aux_language: lang },
            },
          },
        }),
      });
      const response = await fetch(`${base}/back-in-stock-subscriptions/?company_id=${key}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.notifyVariant.id}` } },
            },
          },
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      form.hidden = true;
      this.notify.querySelector('[data-notify-done]').hidden = false;
    } catch (e) {
      error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  /* ---------- Subscription ---------- */

  #initSubscription() {
    this.subToggle = this.querySelector('[data-subscribe-toggle]');
    this.planSelect = this.querySelector('[data-selling-plan]');
    const plans = this.querySelector('[data-subscribe-plans]');
    if (!this.subToggle || !this.planSelect) return;

    this.subToggle.addEventListener('change', () => {
      const on = this.subToggle.checked;
      plans.hidden = !on;
      this.planSelect.disabled = !on;
      this.#setPrice();
    });
    this.planSelect.addEventListener('change', () => this.#setPrice());
  }

  #selectedPlanId() {
    return this.planSelect?.value || null;
  }

  #activePlanId() {
    return this.subToggle?.checked ? this.#selectedPlanId() : null;
  }

  /* ---------- Add to bag ---------- */

  #initForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!this.sizeSelected) {
        this.#flagMissingSize();
        return;
      }
      this.#addToCart();
    });
  }

  #flagMissingSize() {
    this.panel?.classList.remove('is-compact');
    const set = this.optionSets.find((s) => !s.querySelector('input:checked')) || this.optionSets[0];
    if (!set) return;
    set.classList.add('has-error');
    set.querySelector('[data-size-error]')?.removeAttribute('hidden');
    set.scrollIntoView({ behavior: 'smooth', block: 'center' });
    set.querySelector('input')?.focus({ preventScroll: true });
  }

  async #addToCart() {
    const error = this.querySelector('[data-form-error]');
    error?.setAttribute('hidden', '');
    this.atc.setAttribute('aria-busy', 'true');

    const formData = new FormData(this.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const addUrl = `${(window.Theme?.routes?.cart_add_url || '/cart/add').replace(/\.js$/, '')}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to bag failed');

      const cartUrl = `${(window.Theme?.routes?.cart_url || '/cart').replace(/\.js$/, '')}.js`;
      const cart = await fetch(cartUrl).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aux-product',
          sourceId: this.dataset.sectionId,
          itemCount: 1,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (err) {
      deferred.reject(err);
      this.dispatchEvent(new CartErrorEvent({ error: err.message, code: 'INVALID' }));
      if (error) {
        error.textContent = err.message;
        error.removeAttribute('hidden');
      }
    } finally {
      this.atc.removeAttribute('aria-busy');
    }
  }

  /* ---------- Swatches ---------- */

  #initSwatches() {
    const label = this.querySelector('[data-colour-label]');
    const list = this.querySelector('[data-swatches]');
    if (!label || !list) return;
    list.querySelectorAll('[data-colour]').forEach((link) => {
      link.addEventListener('mouseenter', () => (label.textContent = link.dataset.colour));
      link.addEventListener('focus', () => (label.textContent = link.dataset.colour));
    });
    list.addEventListener('mouseleave', () => (label.textContent = label.dataset.default));
    this.querySelector('[data-swatches-more]')?.addEventListener('click', () => list.classList.add('is-expanded'));
  }

  /* ---------- Description ---------- */

  #initDescription() {
    const wrap = this.querySelector('[data-description]');
    const toggle = this.querySelector('[data-description-toggle]');
    if (!wrap || !toggle) return;
    const more = wrap.querySelector('[data-description-more]');
    const body = wrap.querySelector('[data-description-body]');

    // Hide the toggle when there is nothing more to show.
    requestAnimationFrame(() => {
      const clamped = body && body.scrollHeight > body.clientHeight + 2;
      if (!clamped && !more?.children.length) toggle.hidden = true;
    });

    toggle.addEventListener('click', () => {
      const expanded = !wrap.classList.contains('is-expanded');
      wrap.classList.toggle('is-expanded', expanded);
      if (more) more.hidden = !expanded;
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.querySelector('[data-more-label]').hidden = expanded;
      toggle.querySelector('[data-less-label]').hidden = !expanded;
    });
  }

  /* ---------- Drawers ---------- */

  #initDrawers() {
    this.querySelectorAll('[data-drawer-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const dialog = this.querySelector(`#${CSS.escape(button.dataset.drawerOpen)}`);
        if (!dialog) return;
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
      });
    });
    this.querySelectorAll('[data-aux-drawer]').forEach((dialog) => {
      dialog.addEventListener('close', () => (document.documentElement.style.overflow = ''));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close(); // backdrop click
      });
      dialog.querySelector('[data-drawer-close]')?.addEventListener('click', () => dialog.close());
      this.#initUnits(dialog);
    });
  }

  #initUnits(scope) {
    const guide = scope.querySelector('[data-size-guide]');
    if (!guide) return;
    guide.querySelectorAll('[data-unit]').forEach((button) => {
      button.addEventListener('click', () => {
        const unit = button.dataset.unit;
        guide.querySelectorAll('[data-unit]').forEach((b) => {
          b.classList.toggle('is-active', b === button);
          b.setAttribute('aria-pressed', String(b === button));
        });
        guide.querySelectorAll('[data-cm]').forEach((el) => (el.hidden = unit !== 'cm'));
        guide.querySelectorAll('[data-in]').forEach((el) => (el.hidden = unit !== 'in'));
      });
    });
  }

  /* ---------- Gallery (mobile carousel) ---------- */

  #initGallery() {
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;
    const dots = [...this.querySelectorAll('.aux-gallery__dot')];
    const items = [...track.children];
    const go = (delta) => {
      const index = Math.round(track.scrollLeft / track.clientWidth);
      const next = Math.max(0, Math.min(items.length - 1, index + delta));
      track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
    };
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(1));

    let frame;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        });
      },
      { passive: true }
    );
  }

  /* ---------- Docked add to bag (mobile) ---------- */

  #initDocked() {
    const docked = this.querySelector('[data-docked]');
    if (!docked || !this.atc) return;
    docked.querySelector('[data-docked-atc]')?.addEventListener('click', () => {
      if (!this.sizeSelected) {
        this.#flagMissingSize();
        return;
      }
      this.form.requestSubmit();
    });
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show the docked button only while the real one is below the viewport.
        const below = !entry.isIntersecting && entry.boundingClientRect.top > 0;
        docked.classList.toggle('is-visible', below);
        docked.setAttribute('aria-hidden', String(!below));
        docked.querySelector('button').tabIndex = below ? 0 : -1;
      },
      { threshold: 0 }
    );
    observer.observe(this.atc);
  }

  /* ---------- Compact panel (desktop) ---------- */

  #initCompactPanel() {
    if (!this.classList.contains('aux-product--compactable') || !this.panel) return;
    const firstMedia = this.querySelector('.aux-gallery__item');
    const desktop = window.matchMedia('(min-width: 750px)');
    if (!firstMedia) return;

    let scrolledPast = false;
    let hovering = false;
    const apply = () => this.panel.classList.toggle('is-compact', desktop.matches && scrolledPast && !hovering);

    new IntersectionObserver(
      ([entry]) => {
        scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        apply();
      },
      { threshold: 0 }
    ).observe(firstMedia);

    this.panel.addEventListener('mouseenter', () => {
      hovering = true;
      apply();
    });
    this.panel.addEventListener('mouseleave', () => {
      hovering = false;
      apply();
    });
    this.panel.addEventListener('focusin', () => {
      hovering = true;
      apply();
    });
    desktop.addEventListener('change', apply);
  }
}

if (!customElements.get('aux-product')) customElements.define('aux-product', AuxProduct);
